import { Fragment, useId, useRef, useState, type ReactNode } from "react";

/**
 * Markdown simples (o mesmo produzido pela IA): parágrafos, listas, subtítulos, negrito e itálico.
 * Renderização sem HTML bruto, portanto segura contra injeção de código.
 */

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > last) out.push(text.slice(last, m.index));
    const t = m[0];
    out.push(
      t.startsWith("**") ? <strong key={k++}>{t.slice(2, -2)}</strong> : <em key={k++}>{t.slice(1, -1)}</em>,
    );
    last = m.index! + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className = "reading" }: { text: string; className?: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r/g, "").split("\n");
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) blocks.push(<p key={blocks.length}>{inline(para.join(" "))}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, i) => <li key={i}>{inline(it)}</li>);
    blocks.push(list.ordered ? <ol key={blocks.length}>{items}</ol> : <ul key={blocks.length}>{items}</ul>);
    list = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) {
      flushPara();
      flushList();
    } else if (h) {
      flushPara();
      flushList();
      blocks.push(<h4 key={blocks.length}>{inline(h[2]!)}</h4>);
    } else if (ul || ol) {
      flushPara();
      const ordered = Boolean(ol);
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((ul ?? ol)![1]!);
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return (
    <div className={className}>
      {blocks.map((b, i) => (
        <Fragment key={i}>{b}</Fragment>
      ))}
    </div>
  );
}

/** Editor de texto com formatação básica e pré-visualização. */
export function MarkdownEditor({
  value,
  onChange,
  label,
  error,
  rows = 6,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  error?: string;
  rows?: number;
}) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  function wrap(before: string, after = before, placeholder = "texto") {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const selected = value.slice(s, e) || placeholder;
    onChange(value.slice(0, s) + before + selected + after + value.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + selected.length);
    });
  }
  function prefixLines(prefix: string) {
    const el = ref.current;
    if (!el) return;
    const s = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const e = el.selectionEnd;
    const block = value.slice(s, e) || "item";
    const next = block
      .split("\n")
      .map(
        (l, i) =>
          (prefix === "1. " ? `${i + 1}. ` : prefix) + l.replace(/^(\s*[-*•]\s+|\s*\d+[.)]\s+|#+\s+)/, ""),
      )
      .join("\n");
    onChange(value.slice(0, s) + next + value.slice(e));
    requestAnimationFrame(() => el.focus());
  }

  return (
    <div className="field">
      <div className="md-head">
        <label htmlFor={id}>{label}</label>
        <div className="md-tools" role="toolbar" aria-label={`Formatação de ${label}`}>
          <button type="button" title="Negrito" onClick={() => wrap("**")} disabled={preview}>
            <strong>N</strong>
          </button>
          <button type="button" title="Itálico" onClick={() => wrap("*")} disabled={preview}>
            <em>I</em>
          </button>
          <button type="button" title="Subtítulo" onClick={() => prefixLines("### ")} disabled={preview}>
            T
          </button>
          <button type="button" title="Lista" onClick={() => prefixLines("- ")} disabled={preview}>
            •
          </button>
          <button type="button" title="Lista numerada" onClick={() => prefixLines("1. ")} disabled={preview}>
            1.
          </button>
          <button type="button" className={preview ? "on" : ""} onClick={() => setPreview(!preview)}>
            {preview ? "Editar" : "Visualizar"}
          </button>
        </div>
      </div>
      {preview ? (
        <div className="md-preview">
          <Markdown text={value || "_(vazio)_"} />
        </div>
      ) : (
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}
